import { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import "chartjs-plugin-datalabels";
import Title from "../../../components/titleNew";
import "react-data-table-component-extensions/dist/index.css";
import { arrayBases, converterDataFormatoPadrao } from "../../../util/loja";
import TabelaNext from "../../../components/componentteste/TabelaNext";
import { TituloTabela } from "../../../components/TituloTabelaNew/TituloTabela";
import toast from "react-hot-toast";
import { PermissionsPage } from "../../../util/permissionsPage";
import { crmGestanteService } from "../../../services/crmGestante";
import { useUserData } from "../../../hooks/useUserData";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { LoadingTable } from "../../../components/Loading/LoadingTable";
import { MultiSelectFilterBases } from "../../../components/FilterLojaMetas/MultiSelectFilterBases";
import { FiltroPeriodo } from "../../../components/FiltroPeriodo/FiltroPeriodo";
import { ButtonFilter } from "../../../components/buttonFilter";
import { colunasDesempenho } from "./table";

const DESFECHO_LABEL = {
  START_SALE: "Iniciou Venda",
  SUCCESS: "Sem interesse",
  NO_RESPONSE: "Sem Retorno",
  FOLLOW_UP: "Follow-up",
  AWAITING: "Aguardando",
  OPT_OUT: "Opt-out",
};

const DESFECHO_COLOR = {
  START_SALE: "#16a34a",
  SUCCESS: "#f97316",
  NO_RESPONSE: "#9ca3af",
  FOLLOW_UP: "#3b82f6",
  AWAITING: "#f59e0b",
  OPT_OUT: "#ef4444",
};

const calculateWidth = (data) => {
  if (!data || !data.labels) return 800;
  const min = 800;
  const perLabel = 120;
  return Math.max(min, data.labels.length * perLabel);
};

function buildKpiCards(resumo) {
  return [
    {
      label: "Contatos realizados",
      value: resumo.totalContatos,
      color: "#3b82f6",
    },
    {
      label: "Venda Iniciada",
      value: resumo.totalConversoes,
      color: "#16a34a",
    },
    {
      label: "Conversões",
      value: resumo.vendidasViaCrg ?? 0,
      color: "#0ea5e9",
    },
    {
      label: "Valor Convertido",
      value: (resumo.valorVendidoViaCrg ?? 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      }),
      color: "#0284c7",
    },
    {
      label: "Taxa Conv. (Iniciada)",
      value: `${parseFloat(resumo.taxaConversao || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`,
      color: "#6366f1",
    },
    {
      label: "Taxa Conv. Real",
      value: `${parseFloat(resumo.taxaConversaoReal || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`,
      color: "#8b5cf6",
    },
    {
      label: "Opt-outs",
      value: resumo.totalOptOuts,
      color: "#ef4444",
    },
    {
      label: "Gestantes cadastradas",
      value: resumo.totalGestantesCadastradas,
      color: "#ec4899",
    },
    {
      label: "Resultado pendente",
      value: resumo.totalClientesPendentesResultado ?? 0,
      color: "#d97706",
    },
  ];
}

function buildChartVendedoras(porVendedora) {
  if (!porVendedora || porVendedora.length === 0) return null;
  const labels = porVendedora.map((v) => {
    const nome = v.nomeFunc || `Func. ${v.codFunc}`;
    return nome.split(" ")[0];
  });
  return {
    labels,
    datasets: [
      {
        label: "Contatos",
        data: porVendedora.map((v) => v.totalContatos),
        backgroundColor: "rgba(91, 139, 252, 1)",
        yAxisID: "y-axis-1",
        datalabels: {
          anchor: "end",
          align: "end",
          display: true,
          color: "#000",
          formatter: (v) => v,
        },
      },
      {
        label: "Venda Iniciada",
        data: porVendedora.map((v) => v.conversoes),
        backgroundColor: "#4ade80",
        yAxisID: "y-axis-1",
        datalabels: {
          anchor: "end",
          align: "end",
          display: true,
          color: "#000",
          formatter: (v) => v,
        },
      },
      {
        label: "Conversões",
        data: porVendedora.map((v) => v.vendasFinalizadas || 0),
        valorVendido: porVendedora.map((v) => v.valorVendidoViaCrg || 0),
        backgroundColor: "#0ea5e9",
        yAxisID: "y-axis-1",
        datalabels: {
          anchor: "end",
          align: "end",
          display: true,
          color: "#000",
          formatter: (v) => v,
        },
      },
    ],
  };
}

const chartOptions = {
  maintainAspectRatio: false,
  responsive: false,
  aspectRatio: 1,
  title: {
    display: true,
    padding: 0,
    text: "Contatos e Conversões por Vendedora",
    fontColor: "#FC68A2",
    fontSize: 20,
    align: "left",
  },
  legend: {
    position: "bottom",
    align: "start",
  },
  scales: {
    yAxes: [
      {
        id: "y-axis-1",
        position: "left",
        ticks: { beginAtZero: true },
      },
    ],
    xAxes: [{ ticks: { padding: 25 } }],
  },
  tooltips: {
    mode: "index",
    intersect: false,
    callbacks: {
      label: (tooltipItem, data) => {
        const dataset = data.datasets[tooltipItem.datasetIndex];
        const value = tooltipItem.yLabel;
        if (dataset.label === "Conversões" && dataset.valorVendido) {
          const valor = dataset.valorVendido[tooltipItem.index] ?? 0;
          const valorFmt = Number(valor).toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          });
          return `${dataset.label}: ${value} (${valorFmt})`;
        }
        return `${dataset.label}: ${value}`;
      },
    },
  },
  plugins: {
    datalabels: {
      labels: { title: { font: { size: 11 } } },
    },
  },
};

function CrmGestanteDesempenho() {
  const { userData } = useUserData();
  const accessLevel = userData?.nivelAcesso;
  const acessBase = userData?.baseAcesso;

  const [isLoading, setIsLoading] = useState(true);
  const [periodoInputInicio, setPeriodoInputInicio] = useState("");
  const [periodoInputFim, setPeriodoInputFim] = useState("");
  const [base, setBase] = useState(null);
  const [basesDisponiveis, setBasesDisponiveis] = useState([]);
  const [basesSelecionadas, setBasesSelecionadas] = useState([]);
  const [nivelAcesso, setNivelAcesso] = useState([]);
  const [searchParams, setSearchParams] = useState(null);

  const [resumo, setResumo] = useState(null);
  const [porDesfecho, setPorDesfecho] = useState([]);
  const [porVendedora, setPorVendedora] = useState([]);
  const [dadosGrafico, setDadosGrafico] = useState(null);
  const [dadosParaTabela, setDadosParaTabela] = useState([]);
  const [dadosDisponiveis, setDadosDisponiveis] = useState(true);

  const uniqueKey = useMemo(() => Date.now(), [dadosGrafico]);

  useEffect(() => {
    if (nivelAcesso.length === 0) {
      buscarNivelAcesso();
      setIsLoading(true);
    }
  }, [userData]);

  useMemo(() => {
    preencherDados();
  }, [searchParams]);

  useEffect(() => {
    preencherInputPeriodo();
  }, []);

  const buscarNivelAcesso = () => {
    if (accessLevel) {
      setNivelAcesso(accessLevel);
      const bases = arrayBases(acessBase);
      setBasesDisponiveis(bases);
      if (bases?.length > 0) {
        const baseTemp = bases[0]?.value || bases[0]?.codBase;
        setBase([baseTemp]);
        setBasesSelecionadas([baseTemp]);
      } else {
        setBase(null);
        setBasesSelecionadas([]);
      }
    } else {
      setIsLoading(false);
    }
  };

  function preencherInputPeriodo() {
    const dataHoje = new Date();
    const [inicioMes, hoje] = converterDataFormatoPadrao(dataHoje);
    setPeriodoInputInicio(inicioMes);
    setPeriodoInputFim(hoje);
    iniciarFiltro({ startDate: inicioMes, finalDate: hoje, bases: [] });
  }

  function iniciarFiltro(defaultValue = null) {
    if (defaultValue) {
      return setSearchParams(defaultValue);
    }
    setSearchParams({
      startDate: periodoInputInicio,
      finalDate: periodoInputFim,
      bases: basesSelecionadas,
    });
  }

  async function preencherDados() {
    if (!searchParams) return;
    setIsLoading(true);
    window.scrollTo(0, 0);
    const toastId = toast.loading("Carregando relatório...");
    try {
      const response = await crmGestanteService.relatorioDesempenho({
        startDate: searchParams.startDate,
        finalDate: searchParams.finalDate,
        bases: searchParams.bases || [],
      });

      if (!response?.data) {
        toast.error("Não foi possível carregar os dados", {
          duration: 7000,
          style: { borderRadius: "0.25rem", background: "#333", color: "#fff" },
        });
        setIsLoading(false);
        setDadosDisponiveis(false);
        return;
      }

      if (response.status === 200) {
        toast.success("Dados recebidos", { id: toastId });
        const { resumo: r, porDesfecho: pd, porVendedora: pv } = response.data;

        setResumo(r);
        setPorDesfecho(pd || []);
        setPorVendedora(pv || []);
        setDadosGrafico(buildChartVendedoras(pv || []));

        // Table rows — one per vendedora + resumo
        const total = (pv || []).reduce(
          (acc, v) => ({
            totalContatos: acc.totalContatos + v.totalContatos,
            contatosUnicos: acc.contatosUnicos + (v.contatosUnicos || 0),
            conversoes: acc.conversoes + v.conversoes,
            vendasFinalizadas: acc.vendasFinalizadas + (v.vendasFinalizadas || 0),
            semRetorno: acc.semRetorno + v.semRetorno,
            followUps: acc.followUps + v.followUps,
            aguardando: acc.aguardando + v.aguardando,
            optOuts: acc.optOuts + v.optOuts,
            gestantesUnicasDisponiveis: acc.gestantesUnicasDisponiveis + (v.gestantesUnicasDisponiveis || 0),
            clientesNaFila: acc.clientesNaFila + (v.clientesNaFila || 0),
            clientesPendentesResultado: acc.clientesPendentesResultado + (v.clientesPendentesResultado || 0),
          }),
          { totalContatos: 0, contatosUnicos: 0, conversoes: 0, vendasFinalizadas: 0, semRetorno: 0, followUps: 0, aguardando: 0, optOuts: 0, gestantesUnicasDisponiveis: 0, clientesNaFila: 0, clientesPendentesResultado: 0 }
        );

        const tableRows = (pv || []).map((v) => ({
          nomeFunc: v.nomeFunc || `Func. ${v.codFunc}`,
          codFunc: v.codFunc,
          codLoja: v.codLoja,
          totalContatos: v.totalContatos,
          contatosUnicos: v.contatosUnicos,
          conversoes: v.conversoes,
          vendasFinalizadas: v.vendasFinalizadas || 0,
          taxaConversao: v.taxaConversao,
          semRetorno: v.semRetorno,
          followUps: v.followUps,
          aguardando: v.aguardando,
          optOuts: v.optOuts,
          gestantesUnicasDisponiveis: v.gestantesUnicasDisponiveis,
          clientesNaFila: v.clientesNaFila,
          clientesPendentesResultado: v.clientesPendentesResultado,
        }));

        const totalConv = total.totalContatos > 0 ? (total.vendasFinalizadas / total.totalContatos) * 100 : 0;
        tableRows.push({
          nomeFunc: "Total",
          totalContatos: total.totalContatos,
          contatosUnicos: total.contatosUnicos,
          conversoes: total.conversoes,
          vendasFinalizadas: total.vendasFinalizadas,
          taxaConversao: totalConv,
          semRetorno: total.semRetorno,
          followUps: total.followUps,
          aguardando: total.aguardando,
          optOuts: total.optOuts,
          gestantesUnicasDisponiveis: total.gestantesUnicasDisponiveis,
          clientesNaFila: total.clientesNaFila,
          clientesPendentesResultado: total.clientesPendentesResultado,
        });

        setDadosParaTabela(tableRows);
        setDadosDisponiveis(true);
        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      toast.dismiss(toastId);
      setIsLoading(false);
    }
  }

  const filtrarBases = (value) => {
    setBasesSelecionadas(value);
    setBase(value);
  };

  return (
    PermissionsPage("crmGestanteDesempenho") && (
      <>
        <Box ml={2}>
          <Title title="CENTRAL DE RELACIONAMENTO DA GESTANTE" />
        </Box>

        <Stack gap={2} direction={"row"} mx={2}>
          <Box component={Paper} variant="outlined" px={1} pb={1}>
            <FiltroPeriodo
              periodoInputInicioAux={periodoInputInicio}
              periodoInputFimAux={periodoInputFim}
              setPeriodoInputInicioAux={setPeriodoInputInicio}
              setPeriodoInputFimAux={setPeriodoInputFim}

              titulo="Período dos contatos"
            />
          </Box>
          <Box width={"100%"}>
            <Box pb={1}>
              <MultiSelectFilterBases
                basesDisponiveis={basesDisponiveis}
                basesSelecionadas={basesSelecionadas}
                onChange={filtrarBases}
                disabled={isLoading}
              />
              <Typography variant="caption" component={"span"}>
                *Quando nenhuma base estiver selecionada, todas as bases serão
                retornadas.*
              </Typography>
            </Box>
            <Box>
              <ButtonFilter
                type="button"
                onClick={() => iniciarFiltro()}
                disabled={isLoading}
              >
                Filtrar
              </ButtonFilter>
            </Box>
          </Box>
        </Stack>

        {isLoading ? (
          <Box
            width={"100%"}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            height={"90vh"}
          >
            <LoadingTable isLoading={isLoading} />
          </Box>
        ) : (
          <>
            {/* KPI Cards */}
            {dadosDisponiveis && resumo && (
              <Stack direction="row" gap={2} mx={2} mt={2} flexWrap="wrap">
                {buildKpiCards(resumo).map((card) => (
                  <Box
                    key={card.label}
                    component={Paper}
                    variant="outlined"
                    p={2}
                    minWidth={160}
                    textAlign="center"
                    flex={1}
                  >
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{ color: card.color }}
                    >
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {card.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}

            {/* Desfecho chips */}
            {dadosDisponiveis && porDesfecho.length > 0 && (
              <Stack direction="row" gap={1} mx={2} mt={2} flexWrap="wrap">
                {porDesfecho.map((d) => (
                  <Box
                    key={d.desfecho}
                    component={Paper}
                    variant="outlined"
                    px={1.5}
                    py={0.5}
                    display="flex"
                    alignItems="center"
                    gap={0.75}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: DESFECHO_COLOR[d.desfecho] || "#888",
                      }}
                    />
                    <Typography variant="caption">
                      {DESFECHO_LABEL[d.desfecho] || d.desfecho}:{" "}
                      <strong>{d.quantidade}</strong>
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}

            {/* Chart */}
            {dadosDisponiveis && dadosGrafico && (
              <Box mx={2} mt={2} component={Paper} variant="outlined" p={2}>
                <Box sx={{ overflowX: "scroll" }}>
                  <Bar
                    key={"grafico_desempenho_" + uniqueKey}
                    height={420}
                    width={calculateWidth(dadosGrafico)}
                    data={dadosGrafico}
                    options={chartOptions}
                  />
                </Box>
              </Box>
            )}

            {/* Table */}
            {dadosDisponiveis && dadosParaTabela.length > 0 && (
              <Box m={2} variant="outlined" style={{ overflow: "auto" }}>
                <TituloTabela>Desempenho por Vendedora</TituloTabela>
                <TabelaNext
                  dadosParaTabela={dadosParaTabela}
                  colunas={colunasDesempenho}
                />
              </Box>
            )}
          </>
        )}
      </>
    )
  );
}

export default CrmGestanteDesempenho;
